/**
 * DocTreeItem - Recursive tree item for hierarchical document navigation
 *
 * Features:
 * - Nested document tree rendering via DocTreeNode
 * - Chevron toggle for expand/collapse (only if node has children)
 * - Active document highlighting via activeDocId
 * - Depth-based indentation with vertical guide lines
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { EditorDocument } from '../../api/documents';

export interface DocTreeNode {
  document: EditorDocument;
  children: DocTreeNode[];
}

interface DocTreeItemProps {
  node: DocTreeNode;
  activeDocId?: string;
  onDocumentSelect: (doc: EditorDocument) => void;
  depth?: number;
}

export function DocTreeItem({
  node,
  activeDocId,
  onDocumentSelect,
  depth = 0,
}: DocTreeItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isActive = activeDocId === node.document.id;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSelect = () => {
    onDocumentSelect(node.document);
  };

  return (
    <div
      className={depth > 0 ? 'relative before:absolute before:left-[16px] before:top-0 before:bottom-0 before:w-px before:bg-neutral-100' : ''}
    >
      <button
        onClick={handleSelect}
        className={`
          w-full flex items-center gap-1.5 py-1.5 text-xs rounded-lg transition-all duration-150 group
          ${isActive
            ? 'bg-coral-50 text-coral-700 font-medium'
            : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900'
          }
        `}
        style={{ paddingLeft: `${10 + depth * 14}px`, paddingRight: '10px' }}
      >
        {hasChildren ? (
          <span
            onClick={handleChevronClick}
            className="flex-shrink-0 p-0.5 -ml-0.5 rounded hover:bg-neutral-200/60 transition-colors"
          >
            <ChevronRight
              size={12}
              className={`text-neutral-300 transition-transform duration-200
                ${isExpanded ? 'rotate-90' : ''}`}
            />
          </span>
        ) : (
          <span className="w-[16px] flex-shrink-0" />
        )}
        <span className="flex-1 truncate text-left">{node.document.title}</span>
        <ChevronRight
          size={12}
          className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity
            ${isActive ? 'text-coral-400' : 'text-neutral-300'}`}
        />
      </button>

      {hasChildren && isExpanded && (
        <div className="mt-0.5 space-y-0.5">
          {node.children.map((child) => (
            <DocTreeItem
              key={child.document.id}
              node={child}
              activeDocId={activeDocId}
              onDocumentSelect={onDocumentSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DocTreeItem;
